import type { Cluster } from '@/core/entities/property/Property';

import { calculateH3InscribedRadius } from '@/utils/mapGeometryUtils';
import { computed } from 'vue';

export const CIRCLE_SIZES = {
	SINGLE_DIGIT: 16,
	DOUBLE_DIGITS: 20,
	TRIPLE_DIGITS: 24,
	FOUR_DIGITS: 28,
	FIVE_OR_MORE_DIGITS: 32
} as const;

export const H3_CONFIG = {
	APOTHEM_RATIO: 0.86,
	MIN_SIZE: 96,
	MAX_SIZE: 500,
	DEFAULT_SIZE: 96,
	GOOGLE_MAPS_SCALE: 156543.03392,
	EARTH_RADIUS_METERS: 6371000,
	HEXAGON_MIN_SIZE: 16,
	HEXAGON_MAX_SIZE: 500
} as const;

export const MAP_CONFIG = {
	MIN_ZOOM: 8,
	MAX_ZOOM: 17,
	MIN_ZOOM_LEVEL: 13,
	ZOOM_INCREMENT: 2,
	SELECTION_ZOOM_THRESHOLD: 16,
	MAX_SELECTABLE_PROPERTIES: 20
} as const;

export const MARKER_CONFIG = {
	TOTAL_SIZES: {
		DEFAULT: 96,
		HOVER: 96,
		SELECTED: 315
	},
	COLORS: {
		WHITE: '#FFFFFF',
		BLACK: '#000000',
		GREEN: '#008F33',
		DARK_GREEN: '#00601B'
	},
	SHADOW: {
		OPACITY: { DEFAULT: 0.08, HOVER: 0.12, SELECTED: 0.12 },
		RADIUS: 6
	},
	LABEL: {
		FONT_SIZE: '14px',
		FONT_FAMILY: 'Inter',
		FONT_WEIGHT: '600'
	}
} as const;

type CIRCLE_SIZE = (typeof CIRCLE_SIZES)[keyof typeof CIRCLE_SIZES];

export interface ClusterSize {
	selected: number;
	hovered: number;
}
export function calculateClusterSizes(
	clusters: Cluster[],
	zoom: number
): Map<string, ClusterSize> {
	const sizes = new Map<string, ClusterSize>();

	for (const cluster of clusters) {
		const h3Boundary = cluster.properties.h3Boundary;
		if (h3Boundary?.length > 0) {
			const size = calculateH3InscribedRadius(h3Boundary, zoom);
			sizes.set(cluster.geometry.id, {
				selected: size,
				hovered: size
			});
		}
	}

	return sizes;
}

export function useMapGeometry(getCluster: () => Cluster) {
	const cluster = computed(getCluster);

	function calculatePolygonCenter(
		vertices: [number, number][]
	): [number, number] {
		const totalVertices = vertices.length;
		const sumLat = vertices.reduce((sum, [lat]) => sum + lat, 0);
		const sumLng = vertices.reduce((sum, [, lng]) => sum + lng, 0);
		return [sumLat / totalVertices, sumLng / totalVertices];
	}

	function metersToPixels(meters: number, lat: number, zoom: number): number {
		try {
			const metersPerPixel =
				(H3_CONFIG.GOOGLE_MAPS_SCALE * Math.cos((lat * Math.PI) / 180)) /
				Math.pow(2, zoom);
			return (meters / metersPerPixel) * 2;
		} catch (error) {
			console.warn('Error calculating meters to pixels:', error);
			return 0;
		}
	}

	function greatCircleDistance(
		center: [number, number],
		vertex: [number, number]
	): number {
		const [lat1, lng1] = center;
		const [lat2, lng2] = vertex;

		const dLat = ((lat2 - lat1) * Math.PI) / 180;
		const dLng = ((lng2 - lng1) * Math.PI) / 180;
		const a =
			Math.sin(dLat / 2) * Math.sin(dLat / 2) +
			Math.cos((lat1 * Math.PI) / 180) *
				Math.cos((lat2 * Math.PI) / 180) *
				Math.sin(dLng / 2) *
				Math.sin(dLng / 2);
		return (
			2 *
			Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)) *
			H3_CONFIG.EARTH_RADIUS_METERS
		);
	}

	function calculateSphericalPolygonArea(
		coordinates: [number, number][]
	): number {
		if (!coordinates || coordinates.length < 3) return 0;

		const EARTH_RADIUS = 6378137;

		function toRadians(degrees: number): number {
			return degrees * (Math.PI / 180);
		}

		let area = 0;
		const n = coordinates.length;

		for (let i = 0; i < n; i++) {
			const j = (i + 1) % n;
			const [lat1, lng1] = coordinates[i] || [0, 0];
			const [lat2, lng2] = coordinates[j] || [0, 0];

			const lat1Rad = toRadians(lat1);
			const lat2Rad = toRadians(lat2);
			const deltaLng = toRadians(lng2 - lng1);

			area += deltaLng * (2 + Math.sin(lat1Rad) + Math.sin(lat2Rad));
		}

		return Math.abs((area * EARTH_RADIUS * EARTH_RADIUS) / 2);
	}

	const centerCoordinates = computed(() => {
		if (!cluster.value.geometry?.coordinates) {
			return null;
		}

		const [lat, lng] = cluster.value.geometry.coordinates;
		return { lat, lng };
	});

	const isUnmapped = computed(() => {
		return !cluster.value.properties.pointCount;
	});

	const radiusInMeters = computed<number>(() => {
		const h3Boundary = cluster.value.properties.h3Boundary;

		if (h3Boundary && h3Boundary.length > 0) {
			const areaInSquareMeters = calculateSphericalPolygonArea(h3Boundary);
			return Math.sqrt(areaInSquareMeters / Math.PI);
		}

		const pointCount = cluster.value.properties.pointCount;
		const circleRadius = getCircleRadius(pointCount);
		return circleRadius * 10;
	});

	function getCircleRadius(count: number): CIRCLE_SIZE {
		const digitCount = String(count).length;

		switch (digitCount) {
			case 1:
				return CIRCLE_SIZES.SINGLE_DIGIT;
			case 2:
				return CIRCLE_SIZES.DOUBLE_DIGITS;
			case 3:
				return CIRCLE_SIZES.TRIPLE_DIGITS;
			case 4:
				return CIRCLE_SIZES.FOUR_DIGITS;
			default:
				return CIRCLE_SIZES.FIVE_OR_MORE_DIGITS;
		}
	}

	function areSameMapsValue(
		firstMap: google.maps.Map | null,
		secondMap: google.maps.Map | null
	): boolean {
		const isObject = (val: google.maps.Map | null) =>
			val !== null && typeof val === 'object';
		const areBothNull = firstMap === null && secondMap === null;

		return (isObject(firstMap) && isObject(secondMap)) || areBothNull;
	}

	function manipulateClusterClass(
		clusterInstance:
			| (google.maps.marker.AdvancedMarkerElement & { content?: Element })
			| undefined,
		action: 'add' | 'remove',
		className: string
	): void {
		const element = clusterInstance?.content;
		if (!element) return;

		if (action === 'add') {
			element.classList.add(className);
		} else {
			element.classList.remove(className);
		}
	}

	return {
		centerCoordinates,
		isUnmapped,
		radiusInMeters,
		areSameMapsValue,
		manipulateClusterClass,
		MAP_CONFIG
	};
}
